import { systemGroupType } from "../../../../providers/dataPlatformReportTypes";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * The synthetic Section-2 sub-step that does NOT correspond to a backend
 * `score.systems[]` entry. Its data comes from `workoutPlan` (fueling +
 * training-program tables) and its score is derived from VO2max.
 */
export const ENERGY_TRAINING_SUB_ID = "energy-training";

/**
 * Maps stepper sub-step ids to a substring matched against the backend's
 * `systems[].name`. Narrows the ~24 backend systems down to the 7 main
 * systems described in the consultation spec. Order mirrors the Figma
 * sidebar (node 14711:148285); `energy-training` is appended separately
 * by the sidebar/detail components.
 */
const SUB_TO_NAME: Record<string, string> = {
  heart: "heart",
  nervous: "nervous",
  lungs: "lung",
  respiratory: "respiratory",
  "body-composition": "body comp",
  metabolism: "metabolism",
  mitochondria: "mitochondria",
};

export const SUB_IDS = Object.keys(SUB_TO_NAME);

/**
 * Generic so callers passing `AugmentedSystemGroup[]` get the augmented type
 * back (and thus see the mock-fallback fields like `influencedBy` /
 * `affectsThis` / `likelyExperiencing`). Generic narrows to the exact input
 * shape — falls back to `systemGroupType` when callers pass the raw type.
 */
export const findSystemBySubId = <T extends systemGroupType>(
  systems: T[],
  subId: string
): T | undefined => {
  const needle = SUB_TO_NAME[subId];
  if (!needle) return undefined;
  return systems.find((s) => s?.name?.toLowerCase().includes(needle));
};

export const subIdForSystem = (system: systemGroupType): string | undefined => {
  const name = system.name?.toLowerCase() ?? "";
  for (const [subId, needle] of Object.entries(SUB_TO_NAME)) {
    if (name.includes(needle)) return subId;
  }
  return undefined;
};

export const mainConsultationSystems = (
  systems: systemGroupType[]
): { subId: string; system: systemGroupType }[] => {
  const out: { subId: string; system: systemGroupType }[] = [];
  for (const subId of SUB_IDS) {
    const sys = findSystemBySubId(systems, subId);
    if (sys) out.push({ subId, system: sys });
  }
  return out;
};

/**
 * Backend approval code that means "fully approved". Codes: 0/1 Pending,
 * 2 Hidden, 3 Partially Approved, 4 Approved. Mirrors the score screen, which
 * only renders a system's score + bar when it's fully approved.
 */
export const APPROVED_STATE = 4;

export const isSystemApproved = (system: systemGroupType): boolean =>
  system.approval === APPROVED_STATE;

/**
 * Score-tier colour helpers. Backend `scoreCategory` is in the 1–5 range
 * (see `helper.ts:ScoreCategory` enum). The colour values here mirror
 * Figma node 14711:147323 (HealthScoreBar variants).
 *   1 Severe Limitation → D[60] (#F5223E)
 *   2 Limitation        → W[70] (#F0BD24)
 *   3 Neutral           → P[50] (#6FAAFC)
 *   4 Good              → S[50] (#2EDC93)
 *   5 Excellent         → SUCCESS_ICON_MAIN / S[60] (#20BF7D)
 */
export const TIER_COLOR = (theme: Theme): Record<number, string> => ({
  1: theme.colorTokens.D[60],
  2: theme.colorTokens.W[70],
  3: theme.colorTokens.P[50],
  4: theme.colorTokens.S[50],
  5: theme.colors_ds.SUCCESS_ICON_MAIN,
});

export const TIER_LABEL: Record<number, string> = {
  1: "Severe limitation",
  2: "Limitation",
  3: "Neutral",
  4: "Good",
  5: "Excellent",
};

export const colorForScore = (theme: Theme, score: number): string => {
  if (score >= 85) return theme.colors_ds.SUCCESS_ICON_MAIN;
  if (score >= 70) return theme.colorTokens.S[50];
  if (score >= 55) return theme.colorTokens.P[50];
  if (score >= 40) return theme.colorTokens.W[70];
  return theme.colorTokens.D[60];
};
